import { useState, type ReactNode } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Input,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  storefrontIcon,
} from '@mlm/ui';
import { STOREFRONT_ICON_KEYS, type StorefrontIconKey } from '@mlm/shared';
import { uploadStorefrontAsset, storefrontAssetUrl } from '../lib/api';

/**
 * A typed field in a repeatable row. `key` is a string field on the row object.
 * `image` fields store an R2 object key (uploaded via the storefront asset
 * route); everything else is a plain string. No raw JSON — every shape is a
 * typed control.
 */
export type RowField =
  | { kind: 'text'; key: string; label: string; placeholder?: string; mono?: boolean }
  | { kind: 'textarea'; key: string; label: string; rows?: number; placeholder?: string }
  | { kind: 'select'; key: string; label: string; options: { value: string; label: string }[] }
  | { kind: 'icon'; key: string; label: string }
  | { kind: 'gradient'; key: string; label: string }
  | { kind: 'image'; key: string; label: string };

/**
 * Generic add/remove/reorder editor over an array of flat row objects. Used by
 * every storefront CMS section (hero slides, banners, vouchers, badges, links,
 * stats). The page owns the array state; this renders the typed controls.
 */
export function RepeatableRows<T extends Record<string, unknown>>({
  items,
  fields,
  makeBlank,
  onChange,
  addLabel = 'Add row',
  emptyHint,
  columns = 2,
  preview,
  fixed = false,
  uploadImage,
  imageUrl,
}: {
  items: T[];
  fields: RowField[];
  makeBlank: () => T;
  onChange: (next: T[]) => void;
  addLabel?: string;
  emptyHint?: string;
  columns?: 1 | 2 | 3;
  /** Override the upload/serve pair for `image` fields (see ImageField). */
  uploadImage?: (file: File) => Promise<{ key: string }>;
  imageUrl?: (key: string) => string;
  /** Optional live preview of each row, rendered below its fields. Shows the
   * admin where the row lands on the storefront ("which goes where"). */
  preview?: (row: T) => ReactNode;
  /** Rows are paired by position with code-owned visuals (icons/colors) —
   * hide add/remove/reorder so only the text can change. */
  fixed?: boolean;
}) {
  function update(i: number, key: string, value: string) {
    onChange(items.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)));
  }
  function remove(i: number) {
    onChange(items.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  }

  const gridCls =
    columns === 1 ? 'sm:grid-cols-1' : columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';

  return (
    <div className="space-y-3">
      {items.length === 0 && emptyHint && (
        <p className="text-sm text-muted-foreground">{emptyHint}</p>
      )}
      {items.map((row, i) => (
        <Card key={i}>
          <CardContent className="flex items-start gap-3 py-3">
            <div className="flex-1 space-y-3">
              <div className={`grid grid-cols-1 gap-3 ${gridCls}`}>
                {fields.map((f) => (
                  <FieldControl
                    key={f.key}
                    field={f}
                    value={String(row[f.key] ?? '')}
                    onChange={(v) => update(i, f.key, v)}
                    uploadImage={uploadImage}
                    imageUrl={imageUrl}
                  />
                ))}
              </div>
              {preview && (
                <div>
                  <p className="mb-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    Preview
                  </p>
                  {preview(row)}
                </div>
              )}
            </div>
            {!fixed && (
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  aria-label="Move up"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label="Move down"
                  disabled={i === items.length - 1}
                  onClick={() => move(i, 1)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label="Remove row"
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      {!fixed && (
        <Button variant="outline" size="sm" onClick={() => onChange([...items, makeBlank()])}>
          <Plus className="h-4 w-4" /> {addLabel}
        </Button>
      )}
    </div>
  );
}

function FieldControl({
  field,
  value,
  onChange,
  uploadImage,
  imageUrl,
}: {
  field: RowField;
  value: string;
  onChange: (v: string) => void;
  uploadImage?: (file: File) => Promise<{ key: string }>;
  imageUrl?: (key: string) => string;
}) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground">{field.label}</span>
      <div className="mt-1">
        {field.kind === 'text' && (
          <Input
            className={`h-8 ${field.mono ? 'font-mono' : ''}`}
            value={value}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
        {field.kind === 'textarea' && (
          <Textarea
            rows={field.rows ?? 3}
            value={value}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
        {field.kind === 'select' && (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {field.kind === 'icon' && <IconField value={value} onChange={onChange} />}
        {field.kind === 'gradient' && <GradientField value={value} onChange={onChange} />}
        {field.kind === 'image' && (
          <ImageField value={value} onChange={onChange} upload={uploadImage} toUrl={imageUrl} />
        )}
      </div>
    </label>
  );
}

/** Visual icon picker over the closed `StorefrontIconKey` allow-list: shows the
 * actual lucide glyph next to each key in the dropdown and on the trigger, so
 * the admin picks by sight rather than guessing what a key looks like. */
function IconField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const current = (STOREFRONT_ICON_KEYS as readonly string[]).includes(value)
    ? (value as StorefrontIconKey)
    : null;
  const Current = current ? storefrontIcon(current) : null;
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8">
        <span className="flex items-center gap-2">
          {Current && <Current className="h-4 w-4" />}
          <span className="capitalize">{current ?? 'Select icon'}</span>
        </span>
      </SelectTrigger>
      <SelectContent>
        {STOREFRONT_ICON_KEYS.map((k) => {
          const Icon = storefrontIcon(k);
          return (
            <SelectItem key={k} value={k}>
              <span className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span className="capitalize">{k}</span>
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

/** 8 compass directions for the gradient picker, expressed as CSS angles. */
const GRADIENT_DIRECTIONS = [
  { label: '↑ Top', deg: 0 },
  { label: '↗ Top-right', deg: 45 },
  { label: '→ Right', deg: 90 },
  { label: '↘ Bottom-right', deg: 135 },
  { label: '↓ Bottom', deg: 180 },
  { label: '↙ Bottom-left', deg: 225 },
  { label: '← Left', deg: 270 },
  { label: '↖ Top-left', deg: 315 },
];

/** Parse the simple `linear-gradient(<deg>deg, <hex>, <hex>)` shape this picker
 * emits. Returns null for anything more complex (multi-stop, named colors,
 * radial), which falls back to a raw-CSS text input. */
function parseSimpleGradient(s: string): { deg: number; from: string; to: string } | null {
  const m = s
    .trim()
    .match(
      /^linear-gradient\(\s*([\d.]+)deg\s*,\s*(#[0-9a-fA-F]{3,8})\s*,\s*(#[0-9a-fA-F]{3,8})\s*\)$/,
    );
  if (!m) return null;
  return { deg: Number(m[1]), from: m[2]!, to: m[3]! };
}

/** Color + direction gradient picker (no CSS knowledge needed) with a swatch
 * preview, plus a raw-CSS fallback for gradients too complex to round-trip
 * through the simple picker. */
function GradientField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const parsed = parseSimpleGradient(value);
  const unparseable = value.trim() !== '' && !parsed;
  const cur = parsed ?? { deg: 135, from: '#FF6633', to: '#D4380D' };
  const compose = (next: { deg: number; from: string; to: string }) =>
    onChange(`linear-gradient(${next.deg}deg, ${next.from}, ${next.to})`);

  if (unparseable) {
    return (
      <div className="space-y-1.5">
        <div className="h-8 rounded border" style={{ background: value }} />
        <Input className="h-8 font-mono" value={value} onChange={(e) => onChange(e.target.value)} />
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground"
          onClick={() => compose(cur)}
        >
          Use the simple color picker instead
        </button>
      </div>
    );
  }

  // Keep the active angle selectable even if it came from an imported value
  // that isn't one of the 8 presets (e.g. an old 120deg gradient).
  const dirOptions = GRADIENT_DIRECTIONS.some((d) => d.deg === cur.deg)
    ? GRADIENT_DIRECTIONS
    : [{ label: `${cur.deg}°`, deg: cur.deg }, ...GRADIENT_DIRECTIONS];

  return (
    <div className="space-y-2">
      <div
        className="h-8 rounded border"
        style={{ background: `linear-gradient(${cur.deg}deg, ${cur.from}, ${cur.to})` }}
      />
      <div className="flex items-center gap-2">
        <input
          type="color"
          title="Start color"
          value={cur.from}
          onChange={(e) => compose({ ...cur, from: e.target.value })}
          className="h-8 w-10 flex-none cursor-pointer rounded border bg-white p-0.5"
        />
        <input
          type="color"
          title="End color"
          value={cur.to}
          onChange={(e) => compose({ ...cur, to: e.target.value })}
          className="h-8 w-10 flex-none cursor-pointer rounded border bg-white p-0.5"
        />
        <Select value={String(cur.deg)} onValueChange={(d) => compose({ ...cur, deg: Number(d) })}>
          <SelectTrigger className="h-8 flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {dirOptions.map((d) => (
              <SelectItem key={d.deg} value={String(d.deg)}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

/**
 * Optional image: upload → store the R2 key; preview via the public read.
 *
 * The upload/serve pair is injectable because the two consumers use DIFFERENT
 * public read routes. Storefront assets are served by `GET /storefront/asset`,
 * which only presigns keys reachable from `collectStorefrontImageKeys`; a
 * replicated-page image is not in that set and would 404. Replicated pages pass
 * their own pair rather than forking this component.
 */
function ImageField({
  value,
  onChange,
  upload = uploadStorefrontAsset,
  toUrl = storefrontAssetUrl,
}: {
  value: string;
  onChange: (v: string) => void;
  upload?: (file: File) => Promise<{ key: string }>;
  toUrl?: (key: string) => string;
}) {
  const [local, setLocal] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const src = local ?? (value ? toUrl(value) : null);

  async function pick(file: File) {
    setLocal(URL.createObjectURL(file));
    setUploading(true);
    try {
      const { key } = await upload(file);
      onChange(key);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      {src ? (
        <img src={src} alt="" className="h-12 w-20 rounded border object-cover bg-white" />
      ) : (
        <div className="h-12 w-20 rounded border bg-muted" />
      )}
      <input
        type="file"
        accept="image/*"
        className="text-xs"
        disabled={uploading}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
        }}
      />
      {uploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
      {value && !uploading && (
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-destructive"
          onClick={() => {
            setLocal(null);
            onChange('');
          }}
        >
          Remove
        </button>
      )}
    </div>
  );
}
